import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";

const STATUSES = ["enquiry","pending","confirmed","payment_pending","paid","vehicle_assigned","driver_assigned","trip_started","trip_completed","cancelled","no_show"];

export default function AdminBookings() {
  const [items, setItems] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [open, setOpen] = useState(null); // booking to assign
  const [sel, setSel] = useState({vehicle_id:"", driver_id:""});
  const [quoteOpen, setQuoteOpen] = useState(null);
  const [quote, setQuote] = useState({fare:"", notes:""});

  const load = () => api.get("/bookings").then(r=>setItems(r.data));
  useEffect(()=>{
    load().catch(()=>{});
    api.get("/vehicles").then(r=>setVehicles(r.data)).catch(()=>{});
    api.get("/drivers").then(r=>setDrivers(r.data)).catch(()=>{});
  }, []);

  const setStatus = async (b, status) => {
    try { await api.patch(`/bookings/${b.booking_id}/status`, {status}); toast.success("Status updated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const assign = async () => {
    try { await api.patch(`/bookings/${open.booking_id}/assign`, sel); toast.success("Assigned"); setOpen(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <h1 className="text-3xl font-display font-black tracking-tight">All Bookings</h1>
      <div className="mt-6 overflow-x-auto rounded-2xl bg-white border border-slate-200">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-slate-50">
            <tr className="text-left">
              <th className="p-3">Booking</th><th className="p-3">Customer</th><th className="p-3">Route</th><th className="p-3">Date</th><th className="p-3">Pax</th><th className="p-3">Fare</th><th className="p-3">Status</th><th className="p-3">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.map(b => (
              <tr key={b.booking_id} data-testid={`row-${b.booking_id}`}>
                <td className="p-3 font-mono text-xs">{b.booking_id}</td>
                <td className="p-3"><div className="font-semibold">{b.customer_name}</div><div className="text-xs text-slate-500">{b.customer_mobile}</div></td>
                <td className="p-3">{b.from_place} → {b.to_place}</td>
                <td className="p-3">{b.travel_date}<br/><span className="text-xs text-slate-500">{b.departure_time}</span></td>
                <td className="p-3">{b.passengers}</td>
                <td className="p-3 font-semibold">₹{b.fare.toLocaleString()}</td>
                <td className="p-3">
                  <Select value={b.status} onValueChange={v=>setStatus(b,v)}>
                    <SelectTrigger className="h-8 text-xs" data-testid={`status-${b.booking_id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map(s=><SelectItem key={s} value={s}>{s.replace(/_/g,' ')}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className="p-3">
                  <div className="flex gap-1">
                    {b.status === "enquiry" && (
                      <Button size="sm" className="bg-[#ea580c] hover:bg-orange-700" onClick={()=>{setQuote({fare:b.fare||"", notes:""}); setQuoteOpen(b);}} data-testid={`quote-${b.booking_id}`}>Quote</Button>
                    )}
                    <Button size="sm" variant="outline" onClick={()=>{setSel({vehicle_id:b.vehicle_id||"", driver_id:b.driver_id||""}); setOpen(b);}} data-testid={`assign-${b.booking_id}`}>Assign</Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!open} onOpenChange={o=>!o && setOpen(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Assign Vehicle & Driver</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-semibold">Vehicle</label>
              <Select value={sel.vehicle_id} onValueChange={v=>setSel({...sel,vehicle_id:v})}>
                <SelectTrigger data-testid="assign-vehicle-select"><SelectValue placeholder="Choose vehicle" /></SelectTrigger>
                <SelectContent>{vehicles.map(v=><SelectItem key={v.id} value={v.id}>{v.vehicle_number} · {v.vehicle_type} · {v.seating_capacity}s · {v.ac?"AC":"Non-AC"}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-semibold">Driver</label>
              <Select value={sel.driver_id} onValueChange={v=>setSel({...sel,driver_id:v})}>
                <SelectTrigger data-testid="assign-driver-select"><SelectValue placeholder="Choose driver" /></SelectTrigger>
                <SelectContent>{drivers.map(d=><SelectItem key={d.id} value={d.id}>{d.name} · {d.mobile}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={assign} className="bg-[#ea580c] hover:bg-orange-700" data-testid="assign-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quote dialog for leads */}
      <Dialog open={!!quoteOpen} onOpenChange={o=>!o && setQuoteOpen(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Send Quote — {quoteOpen?.booking_id}</DialogTitle></DialogHeader>
          {quoteOpen && (
            <div className="space-y-3 text-sm">
              <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 text-xs">
                <b>{quoteOpen.customer_name}</b> · {quoteOpen.customer_mobile}<br/>
                {quoteOpen.from_place} → {quoteOpen.to_place} · {quoteOpen.travel_date} · {quoteOpen.passengers} pax · {quoteOpen.ac?"AC":"Non-AC"}<br/>
                {quoteOpen.notes && <span className="text-slate-500">Notes: {quoteOpen.notes}</span>}
              </div>
              <div>
                <label className="text-sm font-semibold">Quoted Fare (₹)</label>
                <Input type="number" value={quote.fare} onChange={e=>setQuote({...quote,fare:e.target.value})} placeholder="e.g. 8500" data-testid="quote-fare-input" />
              </div>
              <div>
                <label className="text-sm font-semibold">Notes (optional)</label>
                <Textarea value={quote.notes} onChange={e=>setQuote({...quote,notes:e.target.value})} placeholder="Includes toll + driver allowance" data-testid="quote-notes-input" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={async ()=>{
              try {
                await api.patch(`/bookings/${quoteOpen.booking_id}/quote`, {fare: parseFloat(quote.fare)||0, notes: quote.notes});
                toast.success("Quote sent"); setQuoteOpen(null); load();
              } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
            }} className="bg-[#ea580c] hover:bg-orange-700" data-testid="quote-send">Send Quote via WhatsApp</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
